/**
 * Penyunting warna arsiran dan pita lembar log, milik proyek.
 *
 * Satu jendela untuk seluruh aplikasi, dan itu disengaja. Warna kolom RES di
 * lembar LRLC, warna kelas di crossplot yang duduk di sebelahnya, dan warna
 * batupasir di lembar cetak CPI adalah satu keputusan yang sama, dibaca
 * berdampingan di atas meja yang sama — dan tiga tempat menyuntingnya adalah
 * tiga kesempatan untuk membuat ketiganya berselisih.
 *
 * Yang belum disentuh tidak disimpan. Sebuah baris yang masih mengikuti
 * bawaannya tertulis "default" dan bukan sebuah heks, sehingga bawaan yang
 * bergeser di rilis berikutnya tetap sampai kepada proyek ini.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ColorPicker } from "./ColorPicker";
import { Button, Loading, Modal, Notice, useToast } from "./ui";
import {
  groupSwatches,
  readLogColors,
  writeLogColors,
  type LogColorPayload,
} from "../lib/logcolors";

export function LogColorEditor({
  projectId,
  mayWrite,
  onClose,
  onSaved,
}: {
  projectId: number;
  mayWrite: boolean;
  onClose: () => void;
  /**
   * Dipanggil setiap kali warna proyek berubah.
   *
   * Lembar yang sedang terbuka menggambar warna yang **turun bersama
   * muatannya**, jadi ia tidak ikut berganti sampai muatannya diminta lagi.
   * Yang tahu bagaimana memintanya lagi adalah layarnya, bukan jendela ini.
   */
  onSaved?: (payload: LogColorPayload) => void;
}) {
  const toast = useToast();
  const [payload, setPayload] = useState<LogColorPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Menunggu jawaban "Reset every colour" — lihat tombolnya di kaki jendela. */
  const [confirming, setConfirming] = useState(false);
  /**
   * Hidup selama jendela ini terpasang.
   *
   * Dipakai **kedua** jalur yang menulis keadaan, bukan hanya pemuatannya:
   * sebuah penyimpanan yang sedang terbang ketika jendelanya ditutup akan
   * memanggil `setPayload` dan `onSaved` pada komponen yang sudah lepas.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    readLogColors(projectId)
      .then((data) => alive.current && setPayload(data))
      .catch((cause) =>
        alive.current && setError(cause instanceof Error ? cause.message : String(cause)),
      );
  }, [projectId]);

  const save = useCallback(
    (stored: Record<string, string>) => {
      setSaving(true);
      writeLogColors(projectId, stored)
        .then((data) => {
          if (!alive.current) return;
          setPayload(data);
          onSaved?.(data);
        })
        .catch((cause) =>
          alive.current
          && toast(cause instanceof Error ? cause.message : String(cause), "error"),
        )
        .finally(() => alive.current && setSaving(false));
    },
    [projectId, onSaved, toast],
  );

  const set = (key: string, value: string | null) => {
    if (!payload) return;
    const next = { ...payload.stored };
    // Menghapus, bukan menulis string kosong: "kembali ke bawaan" dan "warna
    // kosong" adalah dua hal, dan hanya yang pertama punya arti di sini.
    if (value === null) delete next[key];
    else next[key] = value;
    save(next);
  };

  return (
    <Modal
      title="Log sheet colours"
      width={560}
      onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
          {/* Dua ketukan, dan bukan karena kehati-hatian umum: satu ketukan di
              sini membuang setiap warna yang pernah dipilih proyek ini, bagi
              setiap anggotanya, tanpa satu pun jalan kembali. Tombol per baris
              boleh satu ketukan — ia hanya semahal satu warna. */}
          <Button
            variant={confirming ? "primary" : "ghost"}
            disabled={!mayWrite || saving || !payload
                      || Object.keys(payload.stored).length === 0}
            onClick={() => {
              if (!confirming) {
                setConfirming(true);
                return;
              }
              setConfirming(false);
              save({});
            }}
          >
            {confirming
              ? `Discard all ${Object.keys(payload?.stored ?? {}).length} — click again`
              : "Reset every colour"}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}
      {!payload && !error && <Loading label="Reading the colours…" />}
      {payload && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {!mayWrite && (
            <Notice tone="info">
              Your role on this project is read-only, so these are shown but
              cannot be changed.
            </Notice>
          )}
          <span style={{ font: "400 10px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
            These belong to the project, so every member reads the same sheet in
            the same colours. A sheet that is already open keeps the colours it
            was drawn with until it is asked for again.
          </span>
          {groupSwatches(payload.swatches).map((group) => (
            <div key={group.group} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{
                  font: "500 9px var(--font-mono)",
                  color: "var(--ink-6)",
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                }}
              >
                {group.group}
              </span>
              {group.swatches.map((swatch) => {
                const chosen = payload.stored[swatch.key];
                return (
                  <div
                    key={swatch.key}
                    style={{ display: "flex", alignItems: "center", gap: 9 }}
                  >
                    <ColorPicker
                      compact
                      value={payload.colors[swatch.key]}
                      disabled={!mayWrite || saving}
                      label={swatch.label}
                      onChange={(value) => set(swatch.key, value)}
                    />
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                      <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-2)" }}>
                        {swatch.label}
                      </span>
                      {swatch.note && (
                        <span style={{ font: "400 9px/1.35 var(--font-sans)", color: "var(--ink-6)" }}>
                          {swatch.note}
                        </span>
                      )}
                    </div>
                    <HexField
                      label={swatch.label}
                      value={chosen ?? ""}
                      fallback={swatch.default}
                      disabled={!mayWrite || saving}
                      onCommit={(value) => set(swatch.key, value)}
                    />
                    <Button
                      variant="ghost"
                      disabled={!mayWrite || saving || !chosen}
                      onClick={() => set(swatch.key, null)}
                      title={`Back to the default for ${swatch.label}`}
                      style={{ height: 22 }}
                    >
                      Reset
                    </Button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Kotak heks di samping petak warnanya.
 *
 * Petak warna membuka palet dua belas warna aplikasi ini (`lib/correlation.ts`),
 * dan tidak satu pun dari kedua puluh tiga bawaan lembar ada di dalamnya. Tanpa
 * kotak ini, alasan seluruh fitur — perusahaan yang batupasirnya `#c8a165`
 * karena begitulah petanya sejak dua puluh tahun — tidak terlayani sama
 * sekali, meski servernya menerima heks apa pun.
 *
 * Kosong berarti kembali ke bawaan, sama seperti tombol Reset. Yang bukan heks
 * ditolak di sini dan tidak dikirim: perjalanan pergi-pulang hanya untuk
 * memulangkan galat yang sudah bisa dikatakan sekarang adalah perjalanan yang
 * membuat kotaknya terasa lamban tanpa sebab.
 */
function HexField({ label, value, fallback, disabled, onCommit }: {
  label: string;
  value: string;
  fallback: string;
  disabled?: boolean;
  onCommit: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setDraft(value);
    setBad(false);
  }, [value]);

  const commit = () => {
    const text = draft.trim();
    if (text === value) return;
    if (!text) {
      setBad(false);
      onCommit(null);
      return;
    }
    if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(text)) {
      setBad(true);
      return;
    }
    setBad(false);
    onCommit(text.toLowerCase());
  };

  return (
    <input
      aria-label={`Colour of ${label} as hex`}
      value={draft}
      disabled={disabled}
      placeholder="default"
      title={value ? "Chosen for this project" : `Default ${fallback}`}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
        if (event.key === "Escape") {
          setDraft(value);
          setBad(false);
        }
      }}
      style={{
        width: 74,
        flex: "none",
        font: "400 9px var(--font-mono)",
        color: bad ? "var(--rose)" : "var(--ink-6)",
        background: "transparent",
        border: `1px solid ${bad ? "var(--rose)" : "transparent"}`,
        borderRadius: 3,
        padding: "2px 4px",
        textAlign: "right",
      }}
    />
  );
}
